import _ from 'lodash'
import React, {useEffect, useRef, useState} from "react";
import useCountryAxiosInstance from "./hooks/useCountryAxiosInstance";
import AuthenticationService from "./services/AuthenticationService";
import moment from "moment";

const UserNotificationsContext = React.createContext(null);

export function UserNotificationsContextProvider({ children }) {

  const countryApiRequest = useCountryAxiosInstance();

  const userData = AuthenticationService.getUserData();
  const previousNotificationsRef = useRef();
  const [userNotifications, setUserNotifications] = useState(null);
  const [refreshListener, setRefreshListener] = useState(false);

  const refreshNotifications = () => {
    setRefreshListener((prev) => !prev);
  }

  useEffect(() => {
    if (!userData)
      return;

    const params = {
      // Limits used to download only a small portion of notifications - See logic below
      // NOTE: if these parameters remain null, they won't be sent to the server
      limit: null,
      date_from: null,
    };

    // LIMITING LOGIC
    // - If the user already has notifications, we only need to download the new ones (i.e. with a creation date after
    //   the newest one the user has). This is useful when the user already has notifications and only needs to retrieve
    //   newer ones (e.g. after survey creation, download new notifications in order to see worksite tasks
    //   notifications without reloading the page).
    // - Otherwise, we want to download a portion of notifications based on the role of the user
    //     - admin|CS|sales|sales_director --> Limit to last 500 notifications (arbitrary value, adjust as needed)
    //     - <others> --> Limit to last month (arbitrary value, adjust as needed)
    const hardLimitedRoles = ["admin", "CS", "sales", "sales_director"];
    if (userNotifications?.length > 0)
      params.date_from = _.minBy(userNotifications, 'created_at').created_at;
    else if (hardLimitedRoles.includes(userData.role_type))
      params.limit = 500;
    else
      params.date_from = moment().subtract(1, 'month').format('YYYY-MM-DD');

    countryApiRequest.get('/notifications/all_lighter', {params})
      .then((result) => {
        const allnotifications = [...(userNotifications || []), ...result];
        setUserNotifications(allnotifications);
        previousNotificationsRef.current = allnotifications;
      })
      .catch((err) => {
        console.error("Couldn't retrieve user notifications", err);
      })
  }, [JSON.stringify(userData), refreshListener]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!userNotifications || !previousNotificationsRef?.current)
      return;

    // retrieve ids of the notifications to be deleted on DB
    const currentIds = userNotifications.map((n) => n.id);
    const toRemoveIds = previousNotificationsRef.current
      .filter((n) => !currentIds.includes(n.id))
      .map((n) => n.id);

    if (toRemoveIds.length === 0)
      return;

    // delete removed notifications server-side
    const removePromise = userData.role_type === 'admin' && previousNotificationsRef.current?.length === toRemoveIds.length
      ? countryApiRequest.delete('/notifications/user_all')
      : Promise.all(toRemoveIds.map((nid) => countryApiRequest.delete(`/notifications/${nid}`)));
    removePromise.then(() => {
      previousNotificationsRef.current = previousNotificationsRef.current
        .filter((n) => !toRemoveIds.includes(n.id))
    });
  }, [JSON.stringify(userNotifications)]);  // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <UserNotificationsContext.Provider value={{userNotifications, setUserNotifications, refreshNotifications}}>
      {children}
    </UserNotificationsContext.Provider>
  );
}

export default UserNotificationsContext;